// this file is a wrapper with defaults to be used in both API routes and `getServerSideProps` functions
import type { SessionOptions } from 'iron-session';
import { signOut } from '../utility/services/authActions';
import { OktaSessionState, QwaSession, QwaUser } from './types';
import { v4 as uuidv4 } from 'uuid';
import CsrfService from '../utility/services/csrf';
import OktaService from '../utility/services/okta';
import ms from 'ms';
import pMemoize from 'p-memoize';
import type { UserProfile } from '@auth0/nextjs-auth0/client';

export const sessionOptions: SessionOptions = {
  cookieName: 'qwa-session',
  // secure: true should be used in production (HTTPS) but can't be used in development (HTTP)
  cookieOptions: {
    secure: process.env.NODE_ENV === 'production',
  },
  password: process.env.SECRET_COOKIE_PASSWORD as string,
};

// This is where we specify the typings of req.session.*
declare module 'iron-session' {
  // We need to use the name `IronSessionData` to extend the existing type from `iron-session`
  // and we want to keep `QwaSession` a distinct type
  // This minimizes the size needed, since `QwaSession` is a flat object rather than a field
  // this needs to be an interface in order to extend... we can't use a `type` alias
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface IronSessionData extends QwaSession {}
}

// TODO refactor this into Okta service
// const baseUrlPath = process.env.NEXTAUTH_URL;
const baseUrlPath = process.env.NEXT_PUBLIC_OKTA_LOGIN_URL;

// bubble up a config error ASAP
if (!baseUrlPath) {
  throw new Error(`Missing a value for env variable NEXT_PUBLIC_OKTA_LOGIN_URL!`);
}

const memoizedRefresh = pMemoize(OktaService.refreshSession, {
  maxAge: ms('1m'), // this dedupe calls to prevent getting flagged as "replay attacks" by Okta
});

const handleRefreshToken = async (session: QwaSession) => {
  const expires_at = session.okta_expires_at;
  const state = session.okta_state;
  const nowInMillis = Date.now();
  const expiresAtMillis = expires_at ?? nowInMillis;
  const expiresInMillis = expiresAtMillis - nowInMillis;

  const isExpiringSoon =
    // if we don't have an expires_at, but we are in a valid Okta state, assume it's expired
    (!expires_at &&
      state &&
      (OktaSessionState.SESSION_VALID === state || OktaSessionState.SESSION_EXPIRED === state)) ||
    // if we have an expires at, and it's within the threshold time, try to refresh preemptively for a good UX
    // okta's official SDKs will auto refresh 30s before expiration, for reference
    expiresInMillis < ms('10m');

  const isExpired = OktaSessionState.SESSION_EXPIRED === state;

  const refreshToken = session.refresh_token;
  // let refreshResponse: OktaTokenExchangeResponse | undefined = undefined;
  if ((isExpiringSoon || isExpired) && refreshToken) {
    // we have a known issue where we do multiple gets
    // Okta will disable a refresh token if too many requests are sent
    // this limits the refresh calls to avoid invalidating the token

    // clear the cache of expired entries before we start
    // refreshCache.purgeStale();

    // call the API conditionally, or get the response from the cache
    const refreshResponse = await memoizedRefresh(refreshToken);

    if (refreshResponse) {
      const { expires_in, scope, token_type, access_token, id_token, refresh_token } = refreshResponse;
      session.id_token = id_token;
      session.refresh_token = refresh_token;
      session.okta_expires_at = Date.now() + expires_in * 1000; // convert from seconds to milliseconds
    }
  }

  return session;
};

type Auth0UserMetadata = {
  selected_schema?: string;
};

type Auth0AppMetadata = {
  selected_schema?: string;
  available_schemas: string[];
  userTempRoles?: string[];
  looker_id?: number;
  subscription_status?: string;
  qwa_tools_subscription_status?: string;
  userType?: string;
  planId?: string;
  isContentAiSignUp?: boolean;
  isToolsSwitchEmailSent?: boolean;
};

/**
 * Abstraction shim to help migrate from iron-session to Auth0's session
 * @param user
 * @returns A {@code QwaSession} object that can be returned from {@code useSession} or {@code getSession}
 */
export const transformAuth0IntoQwaSession = (auth0User: UserProfile): QwaSession => {
  // due to the custom action in auth0, we now have these fields to use:
  // user?.app_metadata;
  // user?.user_metadata;

  const user = auth0User as UserProfile & {
    app_metadata?: Auth0AppMetadata;
    user_metadata?: Auth0UserMetadata;
    'qwa-roles': [];
    user_id?: string | null | undefined;
  };

  // console.log(`getServerSideProps: ${auth0Session?.user}`, { user: auth0Session?.user });
  // console.log(`getServerSideProps: ${accessToken}`, { accessToken });

  const qwaUser: QwaUser = {
    email: user.email || undefined,
    name:
      user.given_name && user.family_name
        ? `${typeof user.given_name == 'string' ? user.given_name.trim() : user.given_name} ${
            typeof user.family_name == 'string' ? user.family_name.trim() : user.family_name
          }`
        : `${user.given_name || ''}` || user.name || '',
    groups: ((user['qwa-roles'] || []).length ? user['qwa-roles'] : user.app_metadata?.userTempRoles) || [], // TODO where can we store and retrieve this from auth0? from ID token claims?
    okta_user_id: undefined,
    looker_id: user.app_metadata?.looker_id,
    userType: user.app_metadata?.userType,
    subscription_status: user.app_metadata?.subscription_status,
    qwa_tools_subscription_status: user.app_metadata?.qwa_tools_subscription_status,
    planId: user.app_metadata?.planId,
    user_id: user?.sub,
    isContentAiSignUp: user?.app_metadata?.isContentAiSignUp,
    isToolsSwitchEmailSent: user?.app_metadata?.isToolsSwitchEmailSent === true || false,
  };

  const qwaSession: QwaSession = {
    okta_csrf_token: '',
    okta_nonce: '',
    selected_schema: user.app_metadata?.selected_schema || user.user_metadata?.selected_schema || '',
    user: qwaUser,
  };

  return qwaSession;
};
